import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet'
import L from 'leaflet'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../app/hooks'
import { getValueFromResource } from '../../common/timeLiveFunction'
import { ShowToast } from '../../common/Toastr'
import 'leaflet/dist/leaflet.css'

type ClockInCoords = {
    latitude: number
    longitude: number
    capturedAt: Date
}

export type ClockLocationAction = 'clock-in' | 'break-in' | 'break-out' | 'clock-out'

type ClockInLocationModalProps = {
    isOpen: boolean
    requireLocation: boolean
    useNavigatorLocation?: boolean
    action?: ClockLocationAction
    onClose: () => void
    onConfirm: (latitude: number | string, longitude: number | string) => void
}

const ACTION_COPY: Record<ClockLocationAction, { prompt: string; confirm: string }> = {
    'clock-in': {
        prompt: 'Are you sure you want to mark Clock In?',
        confirm: 'Clock In',
    },
    'break-in': {
        prompt: 'Are you sure you want to mark Break In?',
        confirm: 'Break In',
    },
    'break-out': {
        prompt: 'Are you sure you want to mark Break Out?',
        confirm: 'Break Out',
    },
    'clock-out': {
        prompt: 'Are you sure you want to mark Clock Out?',
        confirm: 'Clock Out',
    },
}

const escapeHtml = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const createLocationPinIcon = (label: string) => L.divIcon({
    className: 'clock-in-map-pin',
    iconSize: [220, label ? 78 : 42],
    iconAnchor: [110, 42],
    html: `<div class="clock-in-map-pin-inner">
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z" fill="#1a73e8"/>
            <circle cx="16" cy="16" r="6.5" fill="#ffffff"/>
        </svg>
        ${label ? `<div class="clock-in-map-place">${escapeHtml(label)}</div>` : ''}
    </div>`,
})

const RefreshMap = () => {
    const map = useMap()

    useEffect(() => {
        const first = window.setTimeout(() => map.invalidateSize(), 50)
        const second = window.setTimeout(() => map.invalidateSize(), 250)
        return () => {
            window.clearTimeout(first)
            window.clearTimeout(second)
        }
    }, [map])

    return null
}

const placeNameFromAddress = (address: Record<string, string> | undefined) => {
    if (!address) return ''
    return address.suburb
        || address.neighbourhood
        || address.quarter
        || address.city_district
        || address.residential
        || address.village
        || address.town
        || address.city
        || ''
}

const ClockInLocationModal = ({ isOpen, requireLocation, useNavigatorLocation = true, action = 'clock-in', onClose, onConfirm }: ClockInLocationModalProps) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const [phase, setPhase] = useState<'idle' | 'locating' | 'ready'>('idle')
    const [coords, setCoords] = useState<ClockInCoords | null>(null)
    const [placeName, setPlaceName] = useState('')
    const [now, setNow] = useState(() => new Date())
    const pinIcon = useMemo(() => createLocationPinIcon(placeName), [placeName])
    const onCloseRef = useRef(onClose)
    const onConfirmRef = useRef(onConfirm)
    const requireLocationRef = useRef(requireLocation)
    const failureHandledRef = useRef(false)

    onCloseRef.current = onClose
    onConfirmRef.current = onConfirm
    requireLocationRef.current = requireLocation

    useEffect(() => {
        if (!isOpen) {
            failureHandledRef.current = false
            setPhase('idle')
            setCoords(null)
            setPlaceName('')
            return
        }

        if (!useNavigatorLocation) {
            setPlaceName('')
            setCoords({
                latitude: 0,
                longitude: 0,
                capturedAt: new Date(),
            })
            setPhase('ready')
            return
        }

        let active = true
        setPhase('locating')
        setCoords(null)
        setPlaceName('')

        const fail = (message?: string) => {
            if (!active || failureHandledRef.current) return
            failureHandledRef.current = true
            active = false
            if (requireLocationRef.current) {
                ShowToast(
                    'error',
                    message || 'Location access is blocked. Please allow location sharing in your browser settings and try again.',
                    'error',
                    'toast-top-right'
                )
                onCloseRef.current()
                return
            }
            onConfirmRef.current('0', '0')
        }

        if (!navigator.geolocation) {
            fail('Location is not available in this browser.')
            return
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (!active) return
                setCoords({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    capturedAt: new Date(),
                })
                setPhase('ready')
            },
            (error) => {
                const message = error?.code === 1
                    ? 'Location access is blocked. Please allow location sharing in your browser settings and try again.'
                    : error?.message
                fail(message)
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0,
            }
        )

        return () => {
            active = false
        }
    }, [isOpen, useNavigatorLocation])

    useEffect(() => {
        if (!coords || (Number(coords.latitude) === 0 && Number(coords.longitude) === 0)) return

        const controller = new AbortController()
        const loadPlaceName = async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.latitude}&lon=${coords.longitude}&zoom=17&addressdetails=1`,
                    { signal: controller.signal, headers: { Accept: 'application/json' } }
                )
                if (!response.ok) return
                const data = await response.json()
                const name = placeNameFromAddress(data?.address)
                if (name) setPlaceName(String(name))
            } catch {
                if (controller.signal.aborted) return
            }
        }

        loadPlaceName()
        return () => controller.abort()
    }, [coords])

    useEffect(() => {
        if (!isOpen) return

        setNow(new Date())
        const timerId = window.setInterval(() => setNow(new Date()), 1000)
        return () => window.clearInterval(timerId)
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseRef.current()
        }
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [isOpen])

    if (!isOpen || phase === 'idle') return null

    const capturedLabel = moment(now).format('MMMM D, YYYY h:mm:ss A')
    const copy = ACTION_COPY[action]

    return createPortal(
        <div className="clock-in-map-overlay" role="presentation" onClick={onClose}>
            <div
                className="clock-in-map-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="clock-in-map-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="clock-in-map-canvas">
                    {phase === 'ready' && coords ? (
                        <>
                            <MapContainer
                                center={[coords.latitude, coords.longitude]}
                                zoom={Number(coords.latitude) === 0 && Number(coords.longitude) === 0 ? 3 : 16}
                                minZoom={3}
                                maxZoom={20}
                                style={{ width: '100%', height: '100%' }}
                                zoomControl={true}
                                attributionControl={false}
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                                    maxZoom={20}
                                />
                                <RefreshMap />
                                <Marker position={[coords.latitude, coords.longitude]} icon={pinIcon} />
                            </MapContainer>
                            <div className="clock-in-map-coords">
                                <span>Latitude: {coords.latitude}</span>
                                <span>Longitude: {coords.longitude}</span>
                            </div>
                        </>
                    ) : (
                        <div className="clock-in-map-loading">
                            <span className="clock-in-map-spinner" />
                            <span>{getValueFromResource('Getting your current location...', dispatch, t)}</span>
                        </div>
                    )}
                </div>

                <div id="clock-in-map-title" className="clock-in-map-time">{capturedLabel}</div>
                <div className="clock-in-map-prompt">
                    {getValueFromResource(copy.prompt, dispatch, t)}
                </div>

                <div className="clock-in-map-actions">
                    <button type="button" className="clock-in-map-cancel" onClick={onClose}>
                        {getValueFromResource('Cancel', dispatch, t)}
                    </button>
                    <button
                        type="button"
                        className="clock-in-map-confirm"
                        disabled={phase !== 'ready' || !coords}
                        onClick={() => {
                            if (!coords) return
                            onConfirm(coords.latitude, coords.longitude)
                        }}
                    >
                        {getValueFromResource(copy.confirm, dispatch, t)}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default ClockInLocationModal
